/* ============================================================
   GR- Method — alternating rows engine
   Rows rise in as they enter the viewport (IntersectionObserver,
   once). Numbers count up, videos play only while visible,
   popup player for the build video, cursor-magnetic grid canvas.
   No pinning, no scroll hijacking — natural scrolling.
   ============================================================ */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function norm(w) {
    return w.toLowerCase().replace(/[.,!?;:\"'«»()\[\]]/g, '');
  }

  /* split big statements into word spans + em highlights
     (never letters — Arabic joining must stay intact) */
  function splitStatements(root) {
    [].forEach.call(root.querySelectorAll('[data-big]'), function (h) {
      if (h.__split) return;
      h.__split = true;
      var text = h.textContent.trim();
      var hl = (h.getAttribute('data-highlight') || '').trim().split(/\s+/).map(norm).filter(Boolean);
      var words = text.split(/\s+/).filter(Boolean);
      h.textContent = '';
      words.forEach(function (w, i) {
        var sp = document.createElement('span');
        sp.className = 'w';
        if (hl.length && hl.indexOf(norm(w)) > -1) {
          var em = document.createElement('em');
          em.textContent = w;
          sp.appendChild(em);
        } else {
          sp.textContent = w;
        }
        h.appendChild(sp);
        if (i < words.length - 1) h.appendChild(document.createTextNode(' '));
      });
    });
  }

  function easeOutExpo(t) { return t === 1 ? 1 : 1 - Math.pow(2, -10 * t); }

  function countUp(el) {
    var target = parseFloat(el.getAttribute('data-count') || '');
    if (isNaN(target)) return;
    var pre = el.getAttribute('data-prefix') || '';
    var suf = el.getAttribute('data-suffix') || '';
    if (reduce) { el.textContent = pre + target + suf; return; }
    var t0 = null, dur = 1200;
    function step(t) {
      if (t0 === null) t0 = t;
      var p = Math.min((t - t0) / dur, 1);
      el.textContent = pre + Math.round(target * easeOutExpo(p)) + suf;
      if (p < 1) window.requestAnimationFrame(step);
    }
    window.requestAnimationFrame(step);
  }

  /* ---------- cursor-magnetic grid (from the Cinematic Hero) ---------- */
  function initGridCanvas(root) {
    var canvas = root.querySelector('.grm__grid-canvas');
    if (!canvas) return null;
    var ctx = canvas.getContext('2d');
    if (!ctx) return null;
    var cell = parseInt(canvas.getAttribute('data-cell'), 10) || 72;
    var color = canvas.getAttribute('data-color') || '#C8FF00';
    var strength = Math.min(1, Math.max(0.1, parseFloat(canvas.getAttribute('data-strength')) || 1));
    var baseSvg = root.querySelector('.grm__bg--svg');
    var W = 0, H = 0, raf = null, io = null;
    var mx = 0, my = 0, cx = 0, cy = 0, pull = 0, hover = false;
    var t0 = performance.now();

    function warp(x, y) {
      if (pull <= 0.002) return [x, y];
      var dx = cx - x, dy = cy - y;
      var d = Math.sqrt(dx * dx + dy * dy);
      var R = Math.max(W, H) * 0.32;
      if (d > R || d < 0.001) return [x, y];
      var k = 0.35 * pull * (1 - d / R);
      return [x + dx * k, y + dy * k];
    }

    function strokeLine(pts, width, alpha, dash, off) {
      ctx.beginPath();
      ctx.moveTo(pts[0][0], pts[0][1]);
      for (var i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
      ctx.strokeStyle = color;
      ctx.globalAlpha = alpha;
      ctx.lineWidth = width;
      ctx.lineCap = 'round';
      if (dash) { ctx.setLineDash(dash); ctx.lineDashOffset = off; }
      ctx.stroke();
      if (dash) ctx.setLineDash([]);
      ctx.globalAlpha = 1;
    }

    function draw(now) {
      cx += (mx - cx) * 0.14;
      cy += (my - cy) * 0.14;
      pull += ((hover ? 1 : 0) - pull) * 0.08;
      ctx.clearRect(0, 0, W, H);
      var step = Math.max(18, cell / 3);
      var t = (now - t0) / 1000;
      var x, y, pts;
      for (x = cell; x < W + cell; x += cell) {
        pts = [];
        for (y = 0; y <= H + step; y += step) pts.push(warp(x, y));
        strokeLine(pts, 1, 0.4 * strength, null, 0);
        if (!reduce) strokeLine(pts, 1.5, 0.85 * strength, [26, 120], -((t * 29.2 + x * 0.7) % 146));
      }
      for (y = cell; y < H + cell; y += cell) {
        pts = [];
        for (x = 0; x <= W + step; x += step) pts.push(warp(x, y));
        strokeLine(pts, 1, 0.4 * strength, null, 0);
        if (!reduce) strokeLine(pts, 1.5, 0.85 * strength, [26, 120], -((t * 29.2 + y * 1.1 + 60) % 146));
      }
    }

    function tick(now) { raf = window.requestAnimationFrame(tick); draw(now); }
    function start() { if (!raf) raf = window.requestAnimationFrame(tick); }
    function stop() { if (raf) { window.cancelAnimationFrame(raf); raf = null; } }

    function resize() {
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = canvas.clientWidth; H = canvas.clientHeight;
      canvas.width = Math.max(1, Math.round(W * dpr));
      canvas.height = Math.max(1, Math.round(H * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      draw(performance.now());
    }

    function onMove(e) {
      var r = canvas.getBoundingClientRect();
      if (r.width < 2) return;
      mx = e.clientX - r.left; my = e.clientY - r.top; hover = true;
    }
    function onLeave() { hover = false; }

    if (baseSvg) baseSvg.style.display = 'none';
    resize();
    window.addEventListener('resize', resize, { passive: true });
    if (!reduce) {
      root.addEventListener('mousemove', onMove, { passive: true });
      root.addEventListener('mouseleave', onLeave, { passive: true });
      io = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) start(); else stop();
      }, { threshold: 0 });
      io.observe(canvas);
    }

    return function () {
      stop();
      if (io) io.disconnect();
      root.removeEventListener('mousemove', onMove);
      root.removeEventListener('mouseleave', onLeave);
      window.removeEventListener('resize', resize);
    };
  }

  /* ---------- section ---------- */
  function Method(root) {
    var self = this;
    this.root = root;
    this.rows = [].slice.call(root.querySelectorAll('[data-row]'));
    this.vids = [].slice.call(root.querySelectorAll('video'));
    this.counted = {};
    this.listeners = [];

    /* popup player */
    this.pop = root.querySelector('[data-vidpop]');
    this.popVid = this.pop ? this.pop.querySelector('video') : null;
    this.popX = this.pop ? this.pop.querySelector('[data-vidpop-close]') : null;
    if (this.pop) {
      this.on(root, 'click', function (e) {
        if (!self.root.classList.contains('grm--vidpop')) return;
        if (e.target.closest && e.target.closest('[data-vidpop]')) return;
        var hit = e.target.closest && (e.target.closest('.grm__build-vid-wrap') || e.target.closest('.grm__piece--has-vid'));
        if (hit) self.openPop(hit.querySelector('video'));
      });
      if (this.popX) this.on(this.popX, 'click', function (e) { e.stopPropagation(); self.closePop(); });
      this.on(this.pop, 'click', function (e) { if (e.target === self.pop) self.closePop(); });
      this.on(document, 'keydown', function (e) { if (e.key === 'Escape') self.closePop(); });
    }

    this.gridCleanup = initGridCanvas(root);
    splitStatements(root);
    root.classList.add('is-js');

    /* magnetic glide: a light smoothed translation while the
       section travels through the viewport — the section lags
       a few pixels behind the scroll and settles into place,
       like a soft Lenis feel without hijacking anything.
       Reduced motion = off. */
    this.glide = root.querySelector('.grm__rows');
    this._gRaf = null;
    this._gCur = 0;
    if (this.glide && !reduce && 'IntersectionObserver' in window) {
      this.gliderIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          self.gIn = en.isIntersecting;
          if (self.gIn) self.glideWake();
        });
      }, { rootMargin: '80px 0px' });
      this.gliderIO.observe(root);
      this.on(window, 'scroll', function () { self.glideWake(); }, { passive: true });
      this.on(window, 'resize', function () { self.glideMeasure(); self.glideWake(); }, { passive: true });
      this.glideMeasure();
    }

    /* rows rise in as they enter; videos play only while visible */
    if ('IntersectionObserver' in window) {
      this.rowIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          var row = en.target;
          if (en.isIntersecting) {
            row.classList.add('is-in');
            if (self.glideMeasure) self.glideMeasure();
            [].forEach.call(row.querySelectorAll('[data-count]'), function (n, k) {
              if (!self.counted[row.getAttribute('data-row') + k + n.getAttribute('data-count')]) {
                self.counted[row.getAttribute('data-row') + k + n.getAttribute('data-count')] = true;
                countUp(n);
              }
            });
          }
          [].forEach.call(row.querySelectorAll('video'), function (v) {
            if (en.isIntersecting) {
              if (v.paused && !v.hasAttribute('controls')) { try { v.play(); } catch (e) {} }
            } else if (!v.paused) v.pause();
          });
        });
      }, { threshold: 0.2, rootMargin: '0px 0px -8% 0px' });
      this.rows.forEach(function (r) { self.rowIO.observe(r); });
    } else {
      this.rows.forEach(function (r) { r.classList.add('is-in'); });
    }
  }

  Method.prototype.on = function (t, type, fn, opts) {
    t.addEventListener(type, fn, opts);
    this.listeners.push([t, type, fn, opts]);
  };

  Method.prototype.openPop = function (v) {
    if (!this.pop || !this.popVid || this.pop.classList.contains('is-open')) return;
    if (v) {
      var src = v.currentSrc || v.getAttribute('src') || '';
      if (!src) {
        var so = v.querySelector('source');
        if (so) src = so.getAttribute('src') || '';
      }
      if (src) { this.popVid.src = src; this.popVid.muted = false; }
    }
    this.vids.forEach(function (x) { if (!x.paused) x.pause(); });
    this.pop.classList.add('is-open');
    try { this.popVid.play(); } catch (e) { this.popVid.play().catch(function () {}); }
  };

  Method.prototype.closePop = function () {
    if (!this.pop || !this.pop.classList.contains('is-open')) return;
    this.pop.classList.remove('is-open');
    if (this.popVid && !this.popVid.paused) this.popVid.pause();
  };

  Method.prototype.destroy = function () {
    this.listeners.forEach(function (l) { l[0].removeEventListener(l[1], l[2], l[3]); });
    this.listeners = [];
    if (this.rowIO) this.rowIO.disconnect();
    if (this.gridCleanup) this.gridCleanup();
    if (this._gRaf) { window.cancelAnimationFrame(this._gRaf); this._gRaf = null; }
    if (this.glide) this.glide.style.transform = '';
    if (this.gliderIO) this.gliderIO.disconnect();
  };

  /* ---------- magnetic glide ----------
     Light scroll-magnetism: the rows container is translated by
     a fraction of the distance from the viewport centre, chased
     through a critically-damped smoother. As the section enters
     from below it drifts up slightly slower than the page; as it
     leaves through the top it trails down a touch — then always
     settles at 0 when resting. Pure transform, no hijacking. */
  Method.prototype.glideMeasure = function () {
    var r = this.root.getBoundingClientRect();
    this.gTop = r.top + window.scrollY;
    this.gH = r.height;
    this.gWin = window.innerHeight;
  };

  Method.prototype.glideWake = function () {
    if (this._gRaf || !this.glide) return;
    var self = this;
    this._gRaf = window.requestAnimationFrame(function () { self._gRaf = null; self.glideFrame(); });
  };

  Method.prototype.glideFrame = function () {
    if (!this.glide || !this.gH) return;
    if (!this.gIn) { this._gCur = 0; this.glide.style.transform = ''; return; }
    var center = this.gTop + this.gH / 2 - window.scrollY; /* viewport-space centre */
    var dist = center - this.gWin / 2;                     /* + below centre, - above */
    var target = Math.max(-90, Math.min(90, dist * 0.07)); /* gentle magnetism, capped */
    /* critically-damped chase (tau ~120ms) */
    this._gCur += (target - this._gCur) * 0.16;
    if (Math.abs(target - this._gCur) < 0.1 && Math.abs(this._gCur) < 0.1) {
      this._gCur = 0;
      this.glide.style.transform = '';
      return;
    }
    this.glide.style.transform = 'translate3d(0,' + this._gCur.toFixed(2) + 'px,0)';
    this.glideWake();
  };

  /* ---------------- init ---------------- */
  function init(scope) {
    var nodes = (scope || document).querySelectorAll('[data-gr-method]');
    [].forEach.call(nodes, function (n) {
      if (n.__grm) return;
      n.__grm = new Method(n);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var nodes = (e.target || document).querySelectorAll ? (e.target || document).querySelectorAll('[data-gr-method]') : [];
    [].forEach.call(nodes, function (n) {
      if (n.__grm) { n.__grm.destroy(); n.__grm = null; }
    });
  });
})();
